<template>
    <!-- 根据消息类型选择渲染方式 -->
    <div class="message-row">
        <!-- Tool Request 消息 -->
        <template v-if="message.messageType === 'tool-request' || message.role === 'tool-request'">
            <div class="tool-side">
                <el-avatar :src="aiAvatar" style="flex-shrink: 0;" />
                <div style="margin-left: 10px; flex: 1;">
                    <ToolRequestMessage :tool-calls="parseToolCalls(message.toolCalls)" />
                </div>
            </div>
        </template>

        <!-- Tool Response 消息 (兼容 role='tool' 的历史数据) -->
        <template v-else-if="message.messageType === 'tool' || message.role === 'tool'">
            <div class="tool-side">
                <el-avatar :src="aiAvatar" style="flex-shrink: 0;" />
                <div style="margin-left: 10px; flex: 1;">
                    <!-- 优先使用 toolResponses，否则尝试从 content 解析 -->
                    <ToolResponseMessage :responses="parseToolResponses(message.toolResponses, message.content)" />
                </div>
            </div>
        </template>

        <!-- Tool Confirm 消息 -->
        <template v-else-if="message.messageType === 'tool-confirm' || message.role === 'tool-confirm'">
            <div class="tool-side">
                <el-avatar :src="aiAvatar" style="flex-shrink: 0;" />
                <div style="margin-left: 10px; flex: 1;">
                    <div class="tool-confirm-message">
                        <div class="tool-header">
                            <span class="tool-icon">⚠️</span>
                            <span class="tool-title">需要确认</span>
                        </div>
                        <div class="tool-content">
                            {{ message.content || '请确认是否执行以下工具调用' }}
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- AI 报告附件消息 -->
        <template v-else-if="message.messageType === 'file' || message.role === 'file'">
            <div class="ai-side">
                <el-avatar :src="aiAvatar" style="flex-shrink: 0;" />
                <div class="content file-message-content">
                    <div class="text file-message-text">{{ message.content }}</div>
                    <FileAttachmentMessage
                        v-for="fid in parseFileIds(message.fileIds)"
                        :key="fid"
                        :file-id="fid"
                        :format="message.format || ''" />
                </div>
            </div>
            <div class="ai-side empty-side"></div>
        </template>

        <!-- AI 消息区 -->
        <template v-else-if="message.role !== 'user'">
            <div class="ai-side">
                <el-avatar :src="aiAvatar" style="flex-shrink: 0;" />
                <div class="content" style="margin-left: 10px;">
                    <!-- 加载中状态：显示在最前面 -->
                    <div v-if="message.loading" class="loading-indicator">
                        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                    </div>
                    <!-- 错误状态 -->
                    <div v-if="message.error" class="error-content">{{ message.error }}</div>
                    <!-- 消息内容 -->
                    <div v-if="message.displayContent || message.content" class="text"
                        v-html="formatContent(message.displayContent || message.content)"></div>
                    <!-- 显示 toolCalls 指示器 -->
                    <div v-if="parseToolCalls(message.toolCalls)?.length > 0" class="tool-calls-indicator">
                        <span class="tool-badge">🔧 调用了 {{ parseToolCalls(message.toolCalls).length }} 个工具</span>
                    </div>
                </div>
            </div>
            <div class="ai-side empty-side"></div>
        </template>

        <!-- 用户消息区 -->
        <template v-else>
            <div class="ai-side empty-side"></div>
            <div class="user-side">
                <el-avatar :src="userAvatar" style="flex-shrink: 0;" />
                <div class="content" style="margin-right: 10px;">
                    <div class="text" v-html="formatContent(message.content)"></div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import ToolRequestMessage from './ToolRequestMessage.vue'
import ToolResponseMessage from './ToolResponseMessage.vue'
import FileAttachmentMessage from './FileAttachmentMessage.vue'

defineProps({
    message: {
        type: Object,
        required: true
    },
    userAvatar: {
        type: String,
        default: ''
    },
    aiAvatar: {
        type: String,
        default: ''
    }
})

// 解析 toolCalls（可能是 JSON 字符串或数组）
function parseToolCalls(toolCalls) {
    if (!toolCalls) return []
    if (Array.isArray(toolCalls)) return toolCalls
    if (typeof toolCalls === 'string') {
        try {
            return JSON.parse(toolCalls)
        } catch {
            return []
        }
    }
    return []
}

// 解析 fileIds(逗号分隔的字符串),兼容老数据(null/空)
function parseFileIds(fileIds) {
    if (!fileIds) return []
    if (Array.isArray(fileIds)) return fileIds.filter(Boolean)
    if (typeof fileIds === 'string') {
        return fileIds.split(',').map(s => s.trim()).filter(Boolean)
    }
    return []
}

// 解析 toolResponses（可能是 JSON 字符串或数组），同时处理 content 备用
function parseToolResponses(toolResponses, content) {
    if (!toolResponses && !content) return []
    if (!toolResponses && content) {
        // 降级：content 中直接存放 tool 结果
        // content 可能是普通文本，也可能是 JSON 字符串，统一放入 responseData
        // ToolResponseMessage 会通过 isJson 判断是否需要格式化
        return [{ id: '', name: 'tool', responseData: content }]
    }
    if (Array.isArray(toolResponses)) return toolResponses
    if (typeof toolResponses === 'string') {
        try {
            return JSON.parse(toolResponses)
        } catch {
            return [{ id: '', name: 'tool', responseData: String(toolResponses) }]
        }
    }
    return []
}

// 格式化内容 (简易Markdown)
function formatContent(text) {
    if (!text) return '';
    let html = escapeHtml(text);

    // 表格（必须在转义后处理，将 markdown 表格转为 html 表格）
    html = renderMarkdownTable(html);

    // 代码块
    html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    // 行内代码
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    // 粗体
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // 斜体
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    // 链接
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
    // 换行：先去除首尾空行，再将连续空行压缩为最多2个（保留段落感）
    html = html.trim();
    html = html.replace(/(?:\s*<br\s*\/?>\s*){3,}/gi, '<br><br>');
    html = html.replace(/\n{3,}/g, '\n\n');
    html = html.replace(/\n/g, '<br>');

    return html;
}

// 渲染 Markdown 表格
function renderMarkdownTable(text) {
    const tableRegex = /(\|.+\|)\n(\|[-: |]+\|)\n((?:\|.+\|\n?)+)/g;
    return text.replace(tableRegex, (match, headerLine, separatorLine, bodyLines) => {
        const headers = headerLine.split('|').filter(c => c.trim() !== '');
        const alignments = separatorLine.split('|').filter(c => c.trim() !== '').map(c => {
            c = c.trim();
            if (c.startsWith(':') && c.endsWith(':')) return 'center';
            if (c.endsWith(':')) return 'right';
            return 'left';
        });

        let html = '<div class="md-table-wrapper"><table class="md-table">';

        // 表头
        html += '<thead><tr>';
        headers.forEach((h, i) => {
            const align = alignments[i] || 'left';
            html += `<th style="text-align:${align}">${h.trim()}</th>`;
        });
        html += '</tr></thead>';

        // 表体
        html += '<tbody>';
        const rows = bodyLines.trim().split('\n');
        rows.forEach(row => {
            const cells = row.split('|').filter(c => c.trim() !== '');
            if (cells.length === 0) return;
            html += '<tr>';
            cells.forEach((cell, i) => {
                const align = alignments[i] || 'left';
                html += `<td style="text-align:${align}">${cell.trim()}</td>`;
            });
            html += '</tr>';
        });
        html += '</tbody></table></div>';

        return html;
    });
}

// HTML转义
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
</script>

<style lang="scss" scoped>
.message-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 10px;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.ai-side,
.user-side {
    display: flex;
    align-items: flex-start;
    max-width: 75%;
}

.ai-side {

    // content 在左，avatar 在右（DOM 顺序决定）
    .content {
        background: white;
        padding: 8px 12px;
        border-radius: 12px 12px 12px 4px;
        margin-right: 10px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    :deep(.text) {
        line-height: 1.5;
        word-break: break-all;

        code {
            background: #f5f5f5;
            padding: 2px 5px;
            border-radius: 3px;
            color: #e83e8c;
            font-family: monospace;
        }

        pre {
            background: #2d2d2d;
            color: #f8f8f2;
            padding: 8px;
            border-radius: 6px;
            overflow-x: auto;
            margin: 4px 0;

            code {
                background: none;
                color: inherit;
                padding: 0;
            }
        }

        a {
            color: #1890ff;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        strong {
            font-weight: 600;
        }

        em {
            font-style: italic;
        }
    }
}

.user-side {
    // avatar 在左，content 在右（DOM 顺序决定）
    flex-direction: row-reverse;

    .content {
        background: linear-gradient(135deg, #1890ff, #40a9ff);
        color: white;
        padding: 8px 12px;
        border-radius: 12px 12px 4px 12px;
        margin-left: 10px;
        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
    }

    :deep(.text code) {
        background: rgba(255, 255, 255, 0.2);
        padding: 2px 5px;
        border-radius: 3px;
        font-family: monospace;
    }
}

.empty-side {
    // 空白占位，保持左右对称
    visibility: hidden;
    min-width: 40px;
}

// AI 报告文件消息气泡:与普通 assistant 气泡共享 .content 样式,加少量自定义
.file-message-content {
    min-width: 260px;
}

.file-message-text {
    margin-bottom: 4px;
    line-height: 1.5;
}

.tool-side {
    display: flex;
    align-items: flex-start;
    max-width: 85%;
}

.tool-confirm-message {
    background: #FFF9E6;
    border: 1px solid #FFE58F;
    border-radius: 8px;
    padding: 12px;
}

.tool-confirm-message .tool-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    color: #D46B08;
}

.tool-confirm-message .tool-title {
    font-weight: 600;
    font-size: 13px;
}

.tool-confirm-message .tool-content {
    color: #666;
    font-size: 13px;
}

.tool-calls-indicator {
    margin-top: 8px;
}

.tool-badge {
    display: inline-block;
    background: #E6F7FF;
    border: 1px solid #91D5FF;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 11px;
    color: #1890FF;
}

// Markdown table styles
:deep(.md-table-wrapper) {
    overflow-x: auto;
    margin: 4px 0;
    border-radius: 6px;
    border: 1px solid #e8e8e8;
}

:deep(.md-table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    background: white;

    th {
        background: #f5f7fa;
        padding: 8px 12px;
        border-bottom: 2px solid #e8e8e8;
        font-weight: 600;
        color: #333;
        white-space: nowrap;
    }

    td {
        padding: 7px 12px;
        border-bottom: 1px solid #f0f0f0;
        color: #555;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tbody tr:hover {
        background: #fafafa;
    }
}

.error-content {
    color: #f56c6c;
    font-size: 13px;
    margin-top: 5px;
}

.loading-indicator {
    display: flex;
    gap: 4px;
    margin-bottom: 4px;

    .dot {
        width: 8px;
        height: 8px;
        background: #909399;
        border-radius: 50%;
        animation: bounce 1.4s infinite ease-in-out both;

        &:nth-child(1) {
            animation-delay: -0.32s;
        }

        &:nth-child(2) {
            animation-delay: -0.16s;
        }
    }
}

@keyframes bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}
</style>
